import { Card, type TablePaginationConfig } from "antd"
import { type ColumnsType } from "antd/es/table"
import React, { type ReactNode, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../../../../components/EntityTable/surfaces.ts"
import { TableAddAction } from "../../../../../../../../components/Table/TableAddAction.tsx"
import { TableResultCount } from "../../../../../../../../components/Table/TableResultCount.tsx"
import { useScrolledTable } from "../../../../../../../../hooks/use-scrolled-table.ts"
import { type PermissionProjectPermissions } from "../../../../../../../SettingsPermissions/types/permissions.types.ts"
import type { SubcontractorFormData } from "../../../../../../../SubcontractorsPage/components/SubcontractorForm/subcontractor-form-data.ts"
import { type SubcontractorListItem } from "../../../../../../../SubcontractorsPage/types/types.ts"
import { type ProjectChecklistItem } from "../../../../../../types/project-checklists.types.tsx"
import {
  CHECKLIST_ITEM_PRIORITIES,
  CHECKLIST_ITEM_STATUSES
} from "../../../../../constants/checklist-item-constants.ts"
import { ProjectViewChecklistsMaxNewRow } from "./ProjectViewChecklistsMaxNewRow.tsx"
import { ProjectViewChecklistsMinNewRow } from "./ProjectViewChecklistsMinNewRow.tsx"
import { getColumns } from "./columns.tsx"

interface RowProps
  extends React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLTableRowElement>,
      HTMLTableRowElement
    >,
    Pick<ProjectViewChecklistsListProps, "onSelectRow"> {
  record: ProjectChecklistItem
  showStatusCheckbox?: boolean
  children?: ReactNode[]
}

const NEW_ROW_ID = "__new_checklist_item__"

interface ProjectViewChecklistsListProps {
  userId: string
  projectPermissions: PermissionProjectPermissions
  checklistItems: ProjectChecklistItem[]
  fetching: boolean
  next?: string | null
  /** The page a load-more failed on, offered back. */
  retryNext?: string | null
  count: number
  editableRow?: string
  addSubcontractorAllowed?: boolean
  createAllowed?: boolean
  isAdding?: boolean
  onAddTask: () => void
  onReorderTasks: (orderedIds: string[]) => void
  onLoadMore: () => void
  onSelectRow?: (record: ProjectChecklistItem, action: string) => void
  onPageChange: (page: number) => void
  addNewAddSubcontractor: (
    data: SubcontractorFormData
  ) => Promise<SubcontractorListItem | null>
}

export function ProjectViewChecklistsList({
  userId,
  projectPermissions,
  checklistItems,
  fetching,
  next,
  retryNext,
  count,
  editableRow = "",
  isAdding = false,
  addSubcontractorAllowed,
  createAllowed,
  addNewAddSubcontractor,
  onAddTask,
  onReorderTasks,
  onLoadMore,
  onSelectRow = () => {},
  onPageChange
}: ProjectViewChecklistsListProps) {
  const { t } = useTranslation()
  const moduleAccess = projectPermissions?.checklists
  const moduleDocs = projectPermissions?.docs

  const isLocked = !!editableRow || isAdding

  const deleteAllowed = moduleAccess?.project_checklist_delete
  const editAllowed = moduleAccess?.project_checklist_edit
  const changeStatusAllowed =
    editAllowed && moduleAccess?.project_checklist_change_status_only

  const uploadFileAllowed = moduleDocs?.project_doc_create
  const deleteFileAllowed = moduleDocs?.project_doc_delete
  const deleteForeignFileAllowed =
    deleteFileAllowed && moduleDocs?.project_doc_manage_others

  const tableData = useMemo(() => {
    const results = checklistItems || []

    if (isAdding) {
      const newItem = {
        id: NEW_ROW_ID,
        status: CHECKLIST_ITEM_STATUSES.NOT_STARTED,
        priority: CHECKLIST_ITEM_PRIORITIES.LOW
      } as ProjectChecklistItem

      return [newItem, ...results] as ProjectChecklistItem[]
    }

    return results
  }, [checklistItems, isAdding])

  const columns: ColumnsType<ProjectChecklistItem> = useMemo(
    () =>
      getColumns({
        disabled: isLocked,
        currentUserId: userId,
        isAdding,
        editAllowed,
        deleteAllowed,
        uploadFileAllowed,
        deleteForeignFileAllowed,
        deleteFileAllowed,
        onToggleDone: (id, is_done) =>
          onSelectRow(
            {
              id,
              status: is_done
                ? CHECKLIST_ITEM_STATUSES?.DONE
                : CHECKLIST_ITEM_STATUSES?.NOT_STARTED
            } as ProjectChecklistItem,
            "update"
          ),
        onSelectRow,
        isLocked
      }),
    [
      userId,
      isLocked,
      isAdding,
      deleteAllowed,
      uploadFileAllowed,
      deleteForeignFileAllowed,
      deleteFileAllowed,
      onSelectRow
    ]
  )

  const onChangeTable = useCallback(
    (pagination: TablePaginationConfig) => {
      onPageChange(pagination.current || 1)
    },
    [onPageChange]
  )

  const RowComponent = useCallback(
    ({ record, ...props }: RowProps) => {
      const isAdding = record?.id === NEW_ROW_ID
      const isEditing = record?.id === editableRow

      if (isEditing && changeStatusAllowed)
        return (
          <ProjectViewChecklistsMinNewRow
            onSelectRow={onSelectRow}
            record={record}
            {...props}
          />
        )

      const mode = isAdding ? "create" : isEditing ? "edit" : null

      if (mode) {
        return (
          <ProjectViewChecklistsMaxNewRow
            mode={mode}
            record={record}
            onSelectRow={onSelectRow}
            addSubcontractorAllowed={addSubcontractorAllowed}
            addNewAddSubcontractor={addNewAddSubcontractor}
            {...props}
          />
        )
      }

      return <tr {...props}>{props.children}</tr>
    },
    [
      editableRow,
      changeStatusAllowed,
      addSubcontractorAllowed,
      editAllowed,
      onSelectRow,
      addNewAddSubcontractor
    ]
  )

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: fetching,
    next: next,
    loadMore: onLoadMore,
    disabled: isLocked
  })

  const reorderAllowed = !!editAllowed && !isLocked && tableData?.length > 1

  // Memoised: a fresh object here changes the table's `components` identity on
  // every render, which remounts the body mid-drag.
  const rowSortable = useMemo(
    () =>
      reorderAllowed
        ? {
            items: tableData.map((item) => item?.id),
            onReorder: onReorderTasks,
            handleLabel: t("projectTasks.reorderHandle")
          }
        : undefined,
    [reorderAllowed, tableData, onReorderTasks, t]
  )

  return (
    <TableAddAction
      label={t("table.addRow.task")}
      allowed={!!createAllowed}
      disabled={isLocked}
      onAdd={onAddTask}
    >
      <Card classNames={{ body: "p-0" }} ref={tableRef}>
        <EntityTable<ProjectChecklistItem>
          surface={TABLE_SURFACES.projectChecklists}
          selectionMode="on-demand"
          tab={DEFAULT_TAB}
          rowKey="id"
          size="small"
          rowHoverable={false}
          rowSortable={rowSortable}
          onScroll={onTableScroll}
          className="[&_th]:bg-primary [&_th]:rounded-none [&_th]:text-white"
          columns={columns}
          loading={fetching && !checklistItems?.length}
          dataSource={tableData}
          scroll={{
            x: 1300,
            y: tableHeight,
            scrollToFirstRowOnChange: true
          }}
          onChange={onChangeTable}
          withOutPagination
          pagination={false}
          onRow={(record) => ({
            record,
            showStatusCheckbox: editAllowed,
            onDoubleClick:
              changeStatusAllowed || editAllowed
                ? () => !isLocked && onSelectRow(record, "edit_row")
                : undefined
          })}
          components={{
            body:
              (changeStatusAllowed || editAllowed || isAdding) &&
              tableData?.length
                ? { row: RowComponent }
                : {}
          }}
        />
        <TableResultCount
          underAddAction
          loaded={checklistItems?.length ?? 0}
          total={count}
          loading={fetching}
          next={next}
          retryNext={retryNext}
          onRetry={onLoadMore}
        />
      </Card>
    </TableAddAction>
  )
}
